import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {useAuth} from './AuthContext'; // Import the useAuth hook

const ProjectContext = createContext();
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.cuecumber.com.au/webmanage';
// const BASE_URL = 'http://192.168.200.184:3005';
const VISION_URL = process.env.REACT_APP_VISION_URL || 'https://www.cuecumber.com.au/vision_server';
// const AI_URL = 'http://192.168.200.184:3010';

export const ProjectProvider = ({ children }) => {
    const { user } = useAuth(); // Use the useAuth hook to get the user
    const [projects, setProjects] = useState([]);
    const [projectData, setProjectData] = useState(null);
    const [choices, setChoices] = useState([]);
    const [isEditMode, setIsEditMode] = useState(true);
    const [projectId, setProjectId] = useState(null);
    const [projectName, setProjectName] = useState(null);
    const [cropInfo, setCropInfo] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // Removed unnecessary effect for isUpdating state

    const setProjectDataWithTrace = (data) => {
        // Validate data structure
        if (!data || !data.project) {
            console.error('Invalid project data structure:', data);
            return;
        }

        // Ensure project.data exists
        if (!data.project.data) {
            data.project.data = {
                choices: [],
                images: [],
                social: []
            };
        }

        // Only update if data is different
        setProjectData(prevData => {
            if (JSON.stringify(prevData) === JSON.stringify(data)) {
                return prevData; // No change needed
            }
            return data;
        });
    };

    // Removed unnecessary effect that was causing console spam
    const addImageToProject = async (imageData) => {
        const { imageId, imageName, s3Url } = imageData;

        // Create new image entry
        const newImage = {
            _id: imageId,
            name: imageName,
            url: s3Url
        };

        // Update project data atomically
        setProjectData(prevData => {
            if (!prevData || !prevData.project) return prevData;

            const updatedImages = [...prevData.project.data.images, newImage];

            return {
                ...prevData,
                project: {
                    ...prevData.project,
                    data: {
                        ...prevData.project.data,
                        images: updatedImages
                    }
                }
            };
        });

        // Return the new image data
        return newImage;
    };


    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                func(...args);
            }, delay);
        };
    };

    // Reset project data when user logs out
    useEffect(() => {
        if (!user) {
            setProjectData(null);
            setChoices([]);
            setProjectId(null);
            setProjectName(null);
            setIsInitialLoad(true);
        }
    }, [user]);

    useEffect(() => {
        const initializeProjectContext = async () => {
            // Check if user is logged in
            const token = localStorage.getItem('token');
            if (!token) {
                console.log('User not logged in, skipping project initialization');
                return;
            }

            try {
                const lastSelectedProjectId = localStorage.getItem('selectedProject');
                if (!lastSelectedProjectId) {
                    console.log('No previously selected project found');
                    return;
                }

                // Fetch the project details
                try {
                    const projectDetails = await fetchProjectDetails(lastSelectedProjectId);
                    setProjectId(lastSelectedProjectId);
                    setProjectName(projectDetails.project?.name || 'Unnamed Project');
                } catch (error) {
                    console.error('Error fetching last selected project:', error);
                    // Clear the invalid project ID from localStorage
                    localStorage.removeItem('selectedProject');
                    setProjectId(null);
                    setProjectName(null);
                }
            } catch (error) {
                console.error('Error in project context initialization:', error);
            }
        };

        // Only initialize if we have a user and need project data
        if (user && (!projectId || !projectName)) {
            initializeProjectContext();
        }
    }, [user]);

    const fetchObjectId = async () => {
        const response = await fetch(`${BASE_URL}/generate-objectid`);
        return await response.json();
    }

    const uploadImageWithCrop = async (file, cropInfo, projectName) => {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('cropInfo', JSON.stringify(cropInfo));

        try {
            const response = await fetch(`${BASE_URL}/uploadWithCrop/${projectName}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to upload image');
            }

            const imagedata = await response.json();

            const newImage = {
                _id: imagedata.imageId,
                name: imagedata.imageName,
                url: imagedata.s3Url
            };

            const updatedProjectData = {
                ...projectData,
                project: {
                    ...projectData.project,
                    data: {
                        ...projectData.project.data,
                        images: [...projectData.project.data.images, newImage]
                    }
                }
            };

            setProjectData(updatedProjectData);

            return {
                s3Url: imagedata.s3Url,
                imageId: imagedata.imageId,
                imageName: imagedata.imageName
            };
        } catch (error) {
            console.error('Error uploading image:', error);
            throw error;
        }
    };



    const updateProjectDataInDB = async (updatedProjectData) => {
        if (!user) {
            console.error('Cannot update project: user not logged in');
            return;
        }

        if (!updatedProjectData || !updatedProjectData.project) {
            console.error('Invalid project data for update:', updatedProjectData);
            return;
        }

        setIsUpdating(true); // Set to true when update starts
        const token = localStorage.getItem('token');
        
        if (!token) {
            setIsUpdating(false);
            console.error('Cannot update project: authentication token missing');
            return;
        }
        
        try {
            // Ensure project data exists
            if (!updatedProjectData.project.data) {
                updatedProjectData.project.data = {
                    choices: [],
                    images: [],
                    social: []
                };
            }

            const dataToSend = { ...updatedProjectData.project.data };
            delete dataToSend.feedback;

            // Validate project ID
            if (!updatedProjectData.project._id) {
                throw new Error('Project ID is missing');
            }

            const response = await fetch(`${BASE_URL}/projectUpdate/${updatedProjectData.project._id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ project: { data: dataToSend } }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to update project data: ${errorText}`);
            }

            const result = await response.json();
            if (!result.success) {
                throw new Error(result.message || 'Failed to update project data');
            }
        } catch (error) {
            console.error('Error updating project data:', error);
            // You might want to show a notification to the user here
        } finally {
            setIsUpdating(false); // Set to false when update completes
        }
    };


    const debouncedUpdateProjectData = useCallback(
        debounce(async (updatedProjectData) => {
            await updateProjectDataInDB(updatedProjectData);
        }, 2000),
        []
    );

    const [lastSavedData, setLastSavedData] = useState(null);

    useEffect(() => {
        if (projectData && !isInitialLoad && !isUpdating) {
            // Validate project data structure
            if (!projectData.project || !projectData.project.data) {
                console.error('Invalid project data structure in update effect:', projectData);
                return;
            }

            // Only update if data has actually changed
            const currentDataStr = JSON.stringify(projectData);
            const lastSavedDataStr = JSON.stringify(lastSavedData);
            
            if (currentDataStr !== lastSavedDataStr) {
                try {
                    debouncedUpdateProjectData(projectData);
                    setLastSavedData(projectData);
                } catch (error) {
                    console.error('Error in project data update effect:', error);
                }
            }
        }
    }, [projectData, isInitialLoad, isUpdating, debouncedUpdateProjectData, lastSavedData]);


    const fetchProjectDetails = async (projectId) => {
        if (!user) {
            throw new Error('User must be logged in to fetch project details');
        }

        if (!projectId) {
            throw new Error('Project ID is required');
        }

        const token = localStorage.getItem('token');
        if (!token) {
            // Clear any stale project data
            localStorage.removeItem('selectedProject');
            throw new Error('Authentication token is missing');
        }

        try {
            const response = await fetch(`${BASE_URL}/api/projects/${projectId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 404) {
                // Project not found - clear it from local storage
                localStorage.removeItem('selectedProject');
                throw new Error('Project not found');
            }

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    // Token expired or invalid - clear it
                    localStorage.removeItem('token');
                    throw new Error('Authentication failed');
                }
                throw new Error(`Failed to fetch project: ${response.statusText}`);
            }

            let project;
            try {
                project = await response.json();
            } catch (parseError) {
                console.error('Error parsing project data:', parseError);
                throw new Error('Invalid project data received');
            }

            // Validate project structure
            if (!project || !project.project) {
                throw new Error('Invalid project data structure');
            }

            // Ensure project.project.data exists with all required fields
            if (!project.project.data) {
                project.project.data = {};
            }

            // Ensure all required arrays exist
            project.project.data.choices = project.project.data.choices || [];
            project.project.data.images = project.project.data.images || [];
            project.project.data.social = project.project.data.social || [];

            // Set initial load flag before setting project data
            setIsInitialLoad(true);

            // Set project data with validation
            setProjectData(project);
            setChoices(project.project.data.choices);
            setProjectName(project.project.name || 'Unnamed Project');
            
            // Reset the flag after a short delay to allow for the initial data to be set
            setTimeout(() => {
                setIsInitialLoad(false);
            }, 100);
            
            return project;
        } catch (error) {
            console.error('Error fetching project data:', error);
            setIsInitialLoad(false);

            // Don't set empty project data for authentication or not found errors
            if (error.message === 'Authentication failed' || error.message === 'Project not found') {
                throw error;
            }

            // For other errors, initialize with empty data
            const emptyProject = {
                project: {
                    _id: projectId,
                    name: 'Error Loading Project',
                    data: {
                        choices: [],
                        images: [],
                        social: []
                    }
                }
            };
            setProjectData(emptyProject);
            setChoices([]);
            setProjectName('Error Loading Project');
            throw error;
        }
    };

    return (
        <ProjectContext.Provider
            value={{
                fetchProjectDetails,
                updateProjectDataInDB,
                projects,
                setProjects,
                BASE_URL,
                VISION_URL,
                projectData,
                setProjectData: setProjectDataWithTrace,
                choices,
                setChoices,
                isEditMode,
                setIsEditMode,
                projectId,
                setProjectId,
                projectName,
                setProjectName,
                user,
                setCropInfo,
                uploadImageWithCrop,
                isUpdating,
                isInitialLoad,
                fetchObjectId
            }}>
            {children}
        </ProjectContext.Provider>
    );
};

export const useProject = () => useContext(ProjectContext);